import { Graphics, Text, Container } from 'pixi.js';
import { BaseScene } from './BaseScene.js';
import { GameState } from '../core/GameState.js';
import { GameplayScene } from './GameplayScene.js';
import { DebugLog } from '../core/DebugLog.js';
import { GAME_WIDTH, GAME_HEIGHT } from '../main.js';
import classesData from '../data/classes.json';

const CLASS_IDS = Object.keys(classesData);
const CARD_WIDTH = 105;
const CARD_HEIGHT = 170;
const CARD_GAP = 8;
const COLOR_BORDER_NORMAL = 0x334455;
const COLOR_BORDER_SELECTED = 0xffdd44;
const COLOR_BG = 0x111118;

export class ClassSelectScene extends BaseScene {
  init() {
    DebugLog.info('ClassSelectScene', 'Initializing class selection');

    this._selectedIndex = 0;
    this._cards = [];
    this._animTimer = 0;
    this._stars = [];

    // Background with particle stars (M25)
    const bg = new Graphics();
    bg.rect(0, 0, GAME_WIDTH, GAME_HEIGHT);
    bg.fill(0x06060c);
    this.container.addChild(bg);

    // Star particles
    this._starContainer = new Container();
    this.container.addChild(this._starContainer);
    for (let i = 0; i < 60; i++) {
      const star = new Graphics();
      const sx = Math.random() * GAME_WIDTH;
      const sy = Math.random() * GAME_HEIGHT;
      const size = 0.5 + Math.random() * 1.2;
      const brightness = 0.2 + Math.random() * 0.5;
      star.circle(0, 0, size);
      star.fill({ color: 0xffffff, alpha: brightness });
      star.x = sx;
      star.y = sy;
      this._starContainer.addChild(star);
      this._stars.push({
        gfx: star,
        baseAlpha: brightness,
        speed: 0.5 + Math.random() * 2,
        phase: Math.random() * Math.PI * 2,
      });
    }

    // Title
    const title = new Text({
      text: 'CHOOSE YOUR CLASS',
      style: {
        fontFamily: 'Courier New, monospace',
        fontSize: 14,
        fontWeight: 'bold',
        fill: 0xffdd44,
        letterSpacing: 3,
      }
    });
    title.anchor.set(0.5);
    title.x = GAME_WIDTH / 2;
    title.y = 18;
    this.container.addChild(title);

    // Calculate total width for centering
    const totalWidth = CLASS_IDS.length * CARD_WIDTH + (CLASS_IDS.length - 1) * CARD_GAP;
    const startX = (GAME_WIDTH - totalWidth) / 2;
    const cardY = 35;

    // Create cards
    for (let i = 0; i < CLASS_IDS.length; i++) {
      const classId = CLASS_IDS[i];
      const data = classesData[classId];
      const cardX = startX + i * (CARD_WIDTH + CARD_GAP);
      const card = this._createCard(classId, data, cardX, cardY);
      this._cards.push(card);
    }

    // Instructions
    const instructions = new Text({
      text: 'Arrow Keys: Select    Enter: Confirm    Esc: Back',
      style: {
        fontFamily: 'Courier New, monospace',
        fontSize: 8,
        fill: 0x445566,
      }
    });
    instructions.anchor.set(0.5);
    instructions.x = GAME_WIDTH / 2;
    instructions.y = GAME_HEIGHT - 12;
    this.container.addChild(instructions);

    this._updateSelection();

    DebugLog.info('ClassSelectScene', 'Class selection ready');
  }

  /**
   * Create a class card with polished layout (M25).
   */
  _createCard(classId, data, x, y) {
    const classColor = parseInt(data.color, 16);

    // Card background
    const cardBg = new Graphics();
    cardBg.rect(x, y, CARD_WIDTH, CARD_HEIGHT);
    cardBg.fill(COLOR_BG);
    this.container.addChild(cardBg);

    // Card border (drawn separately so we can update it)
    const cardBorder = new Graphics();
    this.container.addChild(cardBorder);

    // Class name in class color
    const nameText = new Text({
      text: data.name.toUpperCase(),
      style: {
        fontFamily: 'Courier New, monospace',
        fontSize: 10,
        fontWeight: 'bold',
        fill: classColor,
        letterSpacing: 1,
      }
    });
    nameText.anchor.set(0.5, 0);
    nameText.x = x + CARD_WIDTH / 2;
    nameText.y = y + 5;
    this.container.addChild(nameText);

    // Large class shape preview (M25)
    const shapeGfx = new Graphics();
    const shapeX = x + CARD_WIDTH / 2;
    const shapeY = y + 30;
    const shapeSize = 12;

    if (classId === 'riftmage') {
      shapeGfx.moveTo(shapeX, shapeY - shapeSize);
      shapeGfx.lineTo(shapeX + shapeSize, shapeY);
      shapeGfx.lineTo(shapeX, shapeY + shapeSize);
      shapeGfx.lineTo(shapeX - shapeSize, shapeY);
      shapeGfx.closePath();
      shapeGfx.fill(classColor);
    } else if (classId === 'phantom') {
      shapeGfx.moveTo(shapeX, shapeY + shapeSize);
      shapeGfx.lineTo(shapeX - shapeSize, shapeY - shapeSize);
      shapeGfx.lineTo(shapeX + shapeSize, shapeY - shapeSize);
      shapeGfx.closePath();
      shapeGfx.fill(classColor);
    } else if (classId === 'voidpriest') {
      shapeGfx.circle(shapeX, shapeY, shapeSize);
      shapeGfx.fill(classColor);
    } else {
      shapeGfx.rect(shapeX - shapeSize, shapeY - shapeSize, shapeSize * 2, shapeSize * 2);
      shapeGfx.fill(classColor);
    }
    this.container.addChild(shapeGfx);

    // 2-line description (M25)
    const descText = new Text({
      text: data.description,
      style: {
        fontFamily: 'Courier New, monospace',
        fontSize: 7,
        fill: 0x889999,
        wordWrap: true,
        wordWrapWidth: CARD_WIDTH - 8,
        lineHeight: 9,
      }
    });
    descText.anchor.set(0.5, 0);
    descText.x = x + CARD_WIDTH / 2;
    descText.y = y + 48;
    this.container.addChild(descText);

    // Stat bars (M25)
    const stats = data.stats;
    const statBarY = y + 76;
    const statDefs = [
      { label: 'HP', value: stats.maxHp, max: 150, color: 0xcc3333 },
      { label: 'SPD', value: stats.speed, max: 120, color: 0x44cccc },
      { label: 'DMG', value: stats.damage * 100, max: 200, color: 0xffaa44 },
      { label: 'DEF', value: stats.defense * 100, max: 30, color: 0x8888cc },
    ];

    const barWidth = 50;
    const barHeight = 4;
    const barStartX = x + 30;

    for (let s = 0; s < statDefs.length; s++) {
      const stat = statDefs[s];
      const by = statBarY + s * 10;

      // Label
      const label = new Text({
        text: stat.label,
        style: {
          fontFamily: 'Courier New, monospace',
          fontSize: 7,
          fill: 0x888888,
        }
      });
      label.x = x + 6;
      label.y = by - 1;
      this.container.addChild(label);

      // Bar background
      const barBg = new Graphics();
      barBg.rect(barStartX, by, barWidth, barHeight);
      barBg.fill(0x222222);
      this.container.addChild(barBg);

      // Bar fill
      const fillPct = Math.min(1, stat.value / stat.max);
      const barFill = new Graphics();
      barFill.rect(barStartX, by, barWidth * fillPct, barHeight);
      barFill.fill(stat.color);
      this.container.addChild(barFill);
    }

    // Ability name and description (M25)
    const ability = data.ability;
    const abilityLabel = new Text({
      text: `[Q] ${ability.name}`,
      style: {
        fontFamily: 'Courier New, monospace',
        fontSize: 7,
        fill: 0xccbb88,
        fontWeight: 'bold',
      }
    });
    abilityLabel.anchor.set(0.5, 0);
    abilityLabel.x = x + CARD_WIDTH / 2;
    abilityLabel.y = y + 120;
    this.container.addChild(abilityLabel);

    const abilityDesc = new Text({
      text: ability.description,
      style: {
        fontFamily: 'Courier New, monospace',
        fontSize: 6,
        fill: 0x778888,
        wordWrap: true,
        wordWrapWidth: CARD_WIDTH - 10,
        lineHeight: 8,
      }
    });
    abilityDesc.anchor.set(0.5, 0);
    abilityDesc.x = x + CARD_WIDTH / 2;
    abilityDesc.y = y + 130;
    this.container.addChild(abilityDesc);

    // Starting weapon name (M25)
    const weaponName = data.startingWeapon || 'none';
    const weaponText = new Text({
      text: 'Weapon: ' + weaponName.replace(/_/g, ' '),
      style: {
        fontFamily: 'Courier New, monospace',
        fontSize: 6,
        fill: 0x6688aa,
      }
    });
    weaponText.anchor.set(0.5, 0);
    weaponText.x = x + CARD_WIDTH / 2;
    weaponText.y = y + CARD_HEIGHT - 18;
    this.container.addChild(weaponText);

    return {
      classId,
      data,
      x,
      y,
      cardBg,
      cardBorder,
      nameText,
      shapeGfx,
      descText,
    };
  }

  /**
   * Update visual selection state of all cards.
   */
  _updateSelection() {
    for (let i = 0; i < this._cards.length; i++) {
      const card = this._cards[i];
      const isSelected = (i === this._selectedIndex);

      card.cardBorder.clear();
      const borderColor = isSelected ? COLOR_BORDER_SELECTED : COLOR_BORDER_NORMAL;
      const borderWidth = isSelected ? 2 : 1;
      card.cardBorder.rect(card.x, card.y, CARD_WIDTH, CARD_HEIGHT);
      card.cardBorder.stroke({ width: borderWidth, color: borderColor });
    }
  }

  update(dt) {
    const input = this.sceneManager.inputManager;

    this._animTimer += dt;

    // Animate stars (M25)
    for (const star of this._stars) {
      star.gfx.alpha = star.baseAlpha * (0.5 + 0.5 * Math.sin(this._animTimer * star.speed + star.phase));
    }

    // Navigate left/right
    if (input.isKeyJustPressed('ArrowLeft') || input.isKeyJustPressed('KeyA')) {
      this._selectedIndex--;
      if (this._selectedIndex < 0) this._selectedIndex = CLASS_IDS.length - 1;
      this._updateSelection();
    }

    if (input.isKeyJustPressed('ArrowRight') || input.isKeyJustPressed('KeyD')) {
      this._selectedIndex++;
      if (this._selectedIndex >= CLASS_IDS.length) this._selectedIndex = 0;
      this._updateSelection();
    }

    // Confirm selection
    if (input.isKeyJustPressed('Enter') || input.isKeyJustPressed('Space')) {
      this._confirm();
    }

    // Escape returns to menu
    if (input.isKeyJustPressed('Escape')) {
      import('./MenuScene.js').then(({ MenuScene }) => {
        this.sceneManager.switchTo(new MenuScene(this.sceneManager, this.eventBus));
      });
      return;
    }

    // Animate selected card border glow (M25)
    const glowAlpha = 0.5 + 0.5 * Math.sin(this._animTimer * 3.5);
    for (let i = 0; i < this._cards.length; i++) {
      if (i === this._selectedIndex) {
        const card = this._cards[i];
        card.cardBorder.clear();

        // Outer glow
        card.cardBorder.rect(card.x - 1, card.y - 1, CARD_WIDTH + 2, CARD_HEIGHT + 2);
        card.cardBorder.stroke({ width: 1, color: COLOR_BORDER_SELECTED, alpha: glowAlpha * 0.4 });

        // Main border
        card.cardBorder.rect(card.x, card.y, CARD_WIDTH, CARD_HEIGHT);
        card.cardBorder.stroke({ width: 2, color: COLOR_BORDER_SELECTED, alpha: 0.6 + glowAlpha * 0.4 });
      }
    }
  }

  _confirm() {
    const classId = CLASS_IDS[this._selectedIndex];
    DebugLog.info('ClassSelectScene', `Selected class: ${classId}`);

    GameState.selectedClass = classId;
    GameState.startNewRun();

    this.sceneManager.switchTo(new GameplayScene(this.sceneManager, this.eventBus));
  }
}
